import { ProductTypeName } from '@electricjs/core_entity-client';

import {
  CSVLanguage,
  CSVLanguages,
} from '@/components/People/helpers/employeeCSVLanguages';
import getProductTypeLabel from './getProductTypeLabel';

const CSV_LANGUAGE_IDENTIFIERS = {
  FR: 'Nom',
  EN: 'Name',
  ES: 'Nombre',
} as const;

export const assetCSVLanguage = (column: string): CSVLanguage | null => {
  if (column === CSV_LANGUAGE_IDENTIFIERS.FR) {
    return CSVLanguages.FR;
  } else if (column === CSV_LANGUAGE_IDENTIFIERS.EN) {
    return CSVLanguages.EN;
  } else if (column === CSV_LANGUAGE_IDENTIFIERS.ES) {
    return CSVLanguages.ES;
  }
  return null;
};

const expectedEnglishAssetsFields = [
  'Name',
  'Asset ID',
  'Asset type',
  'Assigned to',
  'Purchase date',
  'Purchase price',
  'Purchase price currency code',
  'Purchased from',
  'Brand',
  'Model',
  'Serial #',
  'Site',
  'Status',
  'Condition',
  'Notes',
  'Warranty Expiration',
];

const expectedFrenchAssetsFields = [
  'Nom',
  "ID de l'actif",
  "Type d'actif",
  'Assigné à',
  "Date d'achat",
  "Prix d'achat",
  "Code de devise du prix d'achat",
  'Acheté de',
  'Marque',
  'Modèle',
  'N° de série',
  'Site',
  'Statut',
  'État',
  'Notes',
  "Date d'expiration de la garantie",
];

const expectedSpanishAssetsFields = [
  'Nombre',
  'ID del activo',
  'Tipo de activo',
  'Asignado a',
  'Fecha de compra',
  'Precio de compra',
  'Código de moneda del precio de compra',
  'Comprado en',
  'Marca',
  'Modelo',
  'N° de serie',
  'Sitio',
  'Estado',
  'Condición',
  'Notas',
  'Vencimiento de la garantía',
];

export const assetCSVColumnHeadersMap = {
  EN: expectedEnglishAssetsFields,
  FR: expectedFrenchAssetsFields,
  ES: expectedSpanishAssetsFields,
};

// Positional sample rows matching header order:
// name, assetId, type, assignedTo, purchaseDate, purchasePrice, purchasePriceCurrencyCode, purchasedFrom, brand, model, serialNumber, site, status, condition, notes, warrantyExpiration
// Index 2: product type (ProductTypeName raw enum)
// Index 6: purchase price currency code (CurrencyCodeEnum raw API value)
// Index 12: status (AssetStatus raw API value)
// Index 13: condition (AssetCondition raw API value)
const TEMPLATE_SAMPLE_ROWS = [
  [
    'Engineering Laptop',
    'EX-LAPTOP-001',
    'laptop',
    'john.doe@example.com',
    '2023-06-01',
    '3500.00',
    'USD',
    'https://examplestore.com',
    'Apple',
    'MacBook Pro 16',
    'SN1234567890',
    'Headquarters',
    'IN_USE',
    'NEW',
    'Purchase price must not include a comma separator — enter 3500.00 not 3,500.00',
    '2026-06-01',
  ],
  [
    'Finance Desktop Workstation',
    'EX-DT-001',
    'desktop',
    'jane.smith@example.com',
    '2023-04-15',
    '1899.00',
    'CAD',
    'https://examplestore.com',
    'Dell',
    'XPS 8960',
    'SN9876543210',
    'Chicago Office',
    'IN_USE',
    'NEW',
    '',
    '2026-04-15',
  ],
  [
    'Conference Room Monitor',
    'EX-MON-001',
    'monitor',
    '',
    '2022-11-01',
    '649.99',
    'USD',
    'https://examplestore.com',
    'LG',
    '27UK850-W',
    'SN5551234567',
    'New York Office',
    'AVAILABLE',
    'USED_GOOD',
    '',
    '2025-11-01',
  ],
  [
    'Wireless Mouse',
    'EX-MS-001',
    'mouse',
    '',
    '2024-01-10',
    '79.99',
    'USD',
    'https://examplestore.com',
    'Logitech',
    'MX Master 3',
    'SN0001234500',
    'Headquarters',
    'AVAILABLE',
    'NEW',
    '',
    '',
  ],
  [
    'Mechanical Keyboard',
    'EX-KB-001',
    'keyboard',
    '',
    '2024-01-10',
    '129.00',
    'USD',
    'https://examplestore.com',
    'Keychron',
    'K2 Pro',
    'SN0002345600',
    'Headquarters',
    'AVAILABLE',
    'NEW',
    '',
    '',
  ],
  [
    'Keyboard and Mouse Combo Set',
    'EX-KM-001',
    'keyboard_mouse',
    '',
    '2023-09-05',
    '99.00',
    'USD',
    'https://examplestore.com',
    'Logitech',
    'MK470',
    'SN0003456700',
    'Boston Office',
    'AVAILABLE',
    'NEW',
    '',
    '',
  ],
  [
    'USB-C Docking Station',
    'EX-DOCK-001',
    'dock',
    'jane.smith@example.com',
    '2023-08-20',
    '259.00',
    'USD',
    'https://examplestore.com',
    'CalDigit',
    'TS4',
    'SN0004567800',
    'Chicago Office',
    'IN_USE',
    'NEW',
    '',
    '2025-08-20',
  ],
  [
    'Conference Room Speaker',
    'EX-AUD-001',
    'audio',
    '',
    '2022-07-14',
    '199.00',
    'USD',
    'https://examplestore.com',
    'Jabra',
    'Speak 710',
    'SN0005678900',
    'New York Office',
    'AVAILABLE',
    'USED_GOOD',
    '',
    '',
  ],
  [
    'HD Webcam',
    'EX-VID-001',
    'video',
    '',
    '2023-03-22',
    '149.99',
    'USD',
    'https://examplestore.com',
    'Logitech',
    'C920',
    'SN0006789000',
    'Headquarters',
    'AVAILABLE',
    'NEW',
    'The Purchased from column must be a full URL including https:// (e.g. https://store.example.com)',
    '',
  ],
  [
    'Office Desk Phone',
    'EX-PH-001',
    'other_phone',
    'john.doe@example.com',
    '2021-05-18',
    '349.00',
    'USD',
    'https://examplestore.com',
    'Poly',
    'VVX 450',
    'SN0007890100',
    'Headquarters',
    'IN_USE',
    'USED_FAIR',
    '',
    '2024-05-18',
  ],
  [
    'Sales Team Tablet',
    'EX-TAB-001',
    'tablets',
    '',
    '2023-10-30',
    '899.00',
    'USD',
    'https://examplestore.com',
    'Apple',
    'iPad Pro 12.9',
    'SN0008901200',
    'San Francisco Office',
    'AVAILABLE',
    'NEW',
    '',
    '2025-10-30',
  ],
  [
    'Noise-Canceling Headphones',
    'EX-HP-001',
    'headphones',
    '',
    '2024-02-05',
    '279.00',
    'USD',
    'https://examplestore.com',
    'Bose',
    'QuietComfort 45',
    'SN0009012300',
    'Headquarters',
    'AVAILABLE',
    'NEW',
    '',
    '',
  ],
  [
    'Podcast Microphone',
    'EX-MIC-001',
    'microphone',
    '',
    '2022-12-01',
    '129.00',
    'USD',
    'https://examplestore.com',
    'Blue',
    'Yeti',
    'SN0010123400',
    'Headquarters',
    'AVAILABLE',
    'NEW',
    '',
    '',
  ],
  [
    'Laptop Power Adapter',
    'EX-PA-001',
    'power_adapter',
    '',
    '2024-01-15',
    '89.00',
    'USD',
    '',
    'Apple',
    '140W USB-C',
    'SN0011234500',
    'Headquarters',
    'AVAILABLE',
    'NEW',
    'The Purchased from field is optional — leave it blank if unknown',
    '',
  ],
  [
    'Thunderbolt 4 Cable',
    'EX-CAB-001',
    'cable',
    '',
    '2023-11-08',
    '49.99',
    'USD',
    '',
    'Apple',
    'Thunderbolt 4 Pro Cable',
    'SN0012345600',
    'Headquarters',
    'AVAILABLE',
    'NEW',
    '',
    '',
  ],
  [
    'Laptop Stand',
    'EX-ACC-001',
    'accessory',
    '',
    '2023-05-17',
    '69.00',
    'USD',
    'https://examplestore.com',
    'Rain Design',
    'mStand',
    'SN0013456700',
    'Headquarters',
    'AVAILABLE',
    'NEW',
    '',
    '',
  ],
  [
    '24-Port Network Switch',
    'EX-MISC-001',
    'miscellaneous',
    '',
    '2021-09-10',
    '399.00',
    'USD',
    'https://examplestore.com',
    'Cisco',
    'SG350-28',
    'SN0014567800',
    'Headquarters',
    'IN_USE',
    'USED_GOOD',
    '',
    '',
  ],
];

type TemplateValueTranslations = {
  status: Record<string, string>;
  condition: Record<string, string>;
};

export const generateImportTemplateCSVData = (
  format: Record<string, string>,
  valueTranslations: TemplateValueTranslations
): Array<Record<string, string>> => {
  return TEMPLATE_SAMPLE_ROWS.map(row => ({
    [format.name]: row[0],
    [format.assetID]: row[1],
    [format.productType]: getProductTypeLabel(row[2] as ProductTypeName),
    [format.assignedTo]: row[3],
    [format.purchaseDate]: row[4],
    [format.purchasePrice]: row[5],
    [format.purchasePriceCurrencyCode]: row[6],
    [format.purchasedFrom]: row[7],
    [format.brand]: row[8],
    [format.model]: row[9],
    [format.serialNumber]: row[10],
    [format.site]: row[11],
    [format.status]: valueTranslations.status[row[12]] ?? row[12],
    [format.condition]: valueTranslations.condition[row[13]] ?? row[13],
    [format.notes]: row[14],
    [format.warrantyExpirationDate]: row[15],
  }));
};

export const assetCSVLanguageFormat = (
  language: CSVLanguage
): Record<string, string> | undefined => {
  const csvFormat = {
    FR: {
      name: 'Nom',
      assetID: "ID de l'actif",
      productType: "Type d'actif",
      assignedTo: 'Assigné à',
      purchaseDate: "Date d'achat",
      purchasePrice: "Prix d'achat",
      purchasePriceCurrencyCode: "Code de devise du prix d'achat",
      purchasedFrom: 'Acheté de',
      brand: 'Marque',
      model: 'Modèle',
      serialNumber: 'N° de série',
      site: 'Site',
      status: 'Statut',
      condition: 'État',
      notes: 'Notes',
      warrantyExpirationDate: "Date d'expiration de la garantie",
    },
    EN: {
      name: 'Name',
      assetID: 'Asset ID',
      productType: 'Asset type',
      assignedTo: 'Assigned to',
      purchaseDate: 'Purchase date',
      purchasePrice: 'Purchase price',
      purchasePriceCurrencyCode: 'Purchase price currency code',
      purchasedFrom: 'Purchased from',
      brand: 'Brand',
      model: 'Model',
      serialNumber: 'Serial #',
      site: 'Site',
      notes: 'Notes',
      status: 'Status',
      condition: 'Condition',
      warrantyExpirationDate: 'Warranty Expiration',
    },
    ES: {
      name: 'Nombre',
      assetID: 'ID del activo',
      productType: 'Tipo de activo',
      assignedTo: 'Asignado a',
      purchaseDate: 'Fecha de compra',
      purchasePrice: 'Precio de compra',
      purchasePriceCurrencyCode: 'Código de moneda del precio de compra',
      purchasedFrom: 'Comprado en',
      brand: 'Marca',
      model: 'Modelo',
      serialNumber: 'N° de serie',
      site: 'Sitio',
      status: 'Estado',
      condition: 'Condición',
      notes: 'Notas',
      warrantyExpirationDate: 'Vencimiento de la garantía',
    },
  };
  return csvFormat[language as keyof typeof csvFormat];
};
